/**
 * RFC 7807 problem reading — one implementation, structurally decoupled
 * (spec `2026-08-03-action-feedback-system.md` §A0/§A4).
 *
 * The repo re-implemented "get the machine-readable reason out of a failed API
 * call" six times with three mutually incompatible precedences. This module is
 * the single answer, and it deliberately does NOT import {@link ApiError}:
 * `lib/api/http.ts` has to import `lib/feedback/notify.ts` (only that module may
 * touch sonner), `notify` needs `problemKey`, and an `ApiError` import here would
 * close the cycle http → notify → problem-message → http. So the accessor
 * duck-types on `{ status: number; body: unknown }` instead — which is also what
 * makes the module testable without standing up the HTTP layer.
 *
 * `ApiError.body` stays `unknown`; the 41 existing `error.body as …` casts keep
 * compiling. `problemDetails()` is simply the recommended accessor from here on.
 *
 * ## What the backend actually emits
 *
 * Two disjoint conventions, verified against the .NET sources:
 *
 * - `Api/Middleware/TenantResolutionMiddleware.cs:408` and
 *   `Api/Filters/BreakGlassEnforcement.cs:120` put the code in **`type`**, as
 *   `urn:ismshub:problem:<code>`.
 * - `Api/Middleware/ProblemDetailsMiddleware.cs` (`CreateIsmsProblemDetails`)
 *   puts it in **`title`** (`Title = ismsEx.ErrorCode`) while `type` carries a
 *   generic `tools.ietf.org` status URI.
 * - The `code` extension is emitted **nowhere** today. It stays in the
 *   precedence as a forward-compatibility rung so a backend that starts sending
 *   it wins over the `title` convention without a frontend change.
 *
 * Hence the precedence **`type` URN › `code` › `title`** — collision-free,
 * because no response uses two of the three.
 */

import { hasKey, type TranslationKey } from "@/lib/i18n";

/** The backend's problem URN namespace (`TenantResolutionMiddleware.cs:39`). */
const PROBLEM_TYPE_PREFIX = "urn:ismshub:problem:";

/**
 * Shape a `title` must have to be read as a machine code.
 *
 * `AddProblemDetails()` + `UseStatusCodePages()` are active in `Api/Program.cs`
 * without `ConfigureApiBehaviorOptions`, so a bare `NotFound()`, a title-less
 * `Problem()` and every model-binding failure produce human titles —
 * `"Not Found"`, `"One or more validation errors occurred."` — and the
 * authorization filters answer `"Zugriff verweigert"`
 * (`Api/Filters/RequirePermissionAttribute.cs:52-55`). Without this guard all of
 * those reach registry lookups and, worse, would be one typo away from matching
 * one. Backend error codes are snake_case identifiers; nothing else qualifies.
 */
const PROBLEM_CODE_SHAPE = /^[a-z][a-z0-9_]*$/;

/**
 * Error `name`s that mean "the request never reached the server", as opposed to
 * "the server refused it".
 *
 * `fetch` rejects with a `TypeError` when the transport fails; an aborted
 * request (a superseded query passing its `AbortSignal`) surfaces as a
 * `DOMException` named `AbortError`, an `AbortSignal.timeout()` as
 * `TimeoutError`. `NetworkError` is the normalised name `lib/api/http.ts`
 * stamps on a wrapped transport failure so the classification survives whatever
 * shape the runtime happened to throw.
 */
const TRANSPORT_ERROR_NAMES: ReadonlySet<string> = new Set([
  "TypeError",
  "AbortError",
  "TimeoutError",
  "NetworkError",
]);

/**
 * An RFC 7807 problem document as the backend sends it.
 *
 * The index signature is not decoration: `admin/users/userErrors.ts` reads
 * `body.limit` / `body.contact` (`Application/Platform/Limits/TenantLimitService.cs`),
 * the ISMS merge dialog reads `body.current`, the suspension surfaces read
 * `body.impact`. Extensions must survive the trip through this module.
 */
export interface ProblemDetails {
  type?: string;
  title?: string;
  detail?: string;
  status?: number;
  code?: string;
  errors?: Record<string, string[]>;
  [key: string]: unknown;
}

/** A code → translation-key table owned by a feature module. */
export type ProblemRegistry = Readonly<Record<string, TranslationKey>>;

/** The `{ status, body }` shape `ApiError` happens to have — matched, not imported. */
interface ProblemCarrier {
  status: number;
  body: unknown;
}

function isProblemCarrier(error: unknown): error is ProblemCarrier {
  if (typeof error !== "object" || error === null) return false;
  return typeof (error as { status?: unknown }).status === "number";
}

/**
 * True when the failure is a transport failure rather than an HTTP response.
 *
 * MUST be consulted before any problem parsing: a rejected `fetch` carries no
 * status and no body, so every ProblemDetails accessor answers `null` for it and
 * the user would get the same generic sentence a 500 produces — the exact defect
 * §1.3 records.
 */
export function isNetworkError(error: unknown): boolean {
  if (error instanceof TypeError) return true;
  if (typeof error !== "object" || error === null) return false;
  const name = (error as { name?: unknown }).name;
  return typeof name === "string" && TRANSPORT_ERROR_NAMES.has(name);
}

/**
 * The parsed problem document behind a failed API call, or `null` when there is
 * none (a transport failure, a plain-text error body, a non-API error).
 *
 * Returns the body as-is so extensions stay readable; callers must treat it as
 * read-only.
 */
export function problemDetails(error: unknown): ProblemDetails | null {
  if (isNetworkError(error)) return null;
  if (!isProblemCarrier(error)) return null;
  const body = error.body;
  if (typeof body !== "object" || body === null || Array.isArray(body)) return null;
  return body as ProblemDetails;
}

/**
 * The machine-readable refusal code, or `null` when the response carries none.
 *
 * Precedence `type` URN › `code` › `title`; see the module header for why that
 * ordering is collision-free, and {@link PROBLEM_CODE_SHAPE} for why `title` is
 * the only rung that needs a guard.
 */
export function problemCode(error: unknown): string | null {
  const problem = problemDetails(error);
  if (!problem) return null;

  const type = typeof problem.type === "string" ? problem.type.trim() : "";
  if (type.startsWith(PROBLEM_TYPE_PREFIX)) {
    const code = type.slice(PROBLEM_TYPE_PREFIX.length).trim();
    if (code) return code;
  }

  const code = typeof problem.code === "string" ? problem.code.trim() : "";
  if (code) return code;

  const title = typeof problem.title === "string" ? problem.title.trim() : "";
  if (title && PROBLEM_CODE_SHAPE.test(title)) return title;

  return null;
}

/**
 * True when `throwApiError` has ALREADY told the user about this failure, so a
 * call site must stay silent about it (Action-Feedback-System §2.7, §2.2 „nie
 * zwei Meldungen für eine Aktion").
 *
 * Three refusals are handled centrally in `lib/api/http.ts` and are the reason
 * this predicate exists:
 *
 *  - **401** — dispatches `auth:session-expired`; the shell bounces the user
 *    through the BFF login. An inline „Speichern fehlgeschlagen" would flash on
 *    a screen that is already navigating away.
 *  - **409 `tenant_mismatch`** — dispatches the mismatch event; the shell
 *    reloads because this tab's chrome and cache belong to a tenant the server
 *    no longer accepts.
 *  - **423 `tenant_read_only`** — raises the deduplicated read-only toast
 *    (`http.ts:92-95`). This is the one the E2E verification caught: the form
 *    ALSO rendered „Speichern fehlgeschlagen" inline, so one refusal produced
 *    two messages — and the inline one was the less accurate of the pair, since
 *    it invites a retry that can never succeed.
 *
 * `useIsmsTracking`'s `shouldReportRollback` encoded the 423 half of this for
 * the rollback toast; every inline handler needs the same guard, which is why
 * it lives here rather than being spelled out per call site.
 *
 * Structural, like {@link problemDetails} — see §A0 on why this layer must not
 * depend on the `ApiError` class identity.
 */
export function isCentrallyReported(error: unknown): boolean {
  if (!isProblemCarrier(error)) return false;
  if (error.status === 401) return true;
  const code = problemCode(error);
  if (error.status === 409 && code === "tenant_mismatch") return true;
  return error.status === 423 && code === "tenant_read_only";
}

/**
 * The RFC 7807 `errors` map — field name → messages — or `null` when the
 * response carries none.
 *
 * Fed by `[ApiController]` model binding and by explicit `ValidationProblem()`
 * calls. The values are English server text and are NOT user-facing: what a
 * caller does with this is mark the named fields invalid (§2.7), not print it.
 */
export function problemFieldErrors(error: unknown): Record<string, string[]> | null {
  const problem = problemDetails(error);
  const raw = problem?.errors;
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return null;

  const out: Record<string, string[]> = {};
  for (const [field, messages] of Object.entries(raw as Record<string, unknown>)) {
    if (!Array.isArray(messages)) continue;
    const texts = messages.filter((m): m is string => typeof m === "string");
    if (texts.length > 0) out[field] = texts;
  }
  return Object.keys(out).length > 0 ? out : null;
}

/**
 * The translation KEY for a failure — never the translated string.
 *
 * The module-global `t()` must not be read during a render: server-side the
 * locale store is process-global and shared by concurrent requests, client-side
 * it is not part of the `useSyncExternalStore` subscription, so a component that
 * called it would render one locale and never re-render on a switch
 * (`lib/i18n.ts:154-165,466-467`). Returning a key keeps the translation at the
 * call site, with the call site's own reactive `t`:
 *
 * ```ts
 * const { t } = useTranslation();
 * t(problemKey(error, HUB_MASTER_ERRORS, "hub.master.errors.generic"));
 * ```
 *
 * `hasKey` guards the registry hit: a registry entry that points at a key later
 * removed from the catalog would otherwise render `humanizeKey()`'s guess at the
 * user instead of the fallback sentence.
 */
export function problemKey(
  error: unknown,
  registry: ProblemRegistry,
  fallback: TranslationKey,
): TranslationKey {
  const code = problemCode(error);
  if (!code) return fallback;
  const key = registry[code];
  if (key && hasKey(key)) return key;
  return fallback;
}
